import { useMemo } from 'react';
import { useQueries } from '@tanstack/react-query';

import { elementTypesQueryOptions } from '@/features/elements';
import type { ElementType } from '@/features/elements';
import { orgQueryOptions } from '@/features/identity';
import type { OrgRef } from '@/features/identity';

import * as searchApi from '../api/searchApi';
import {
  candidateOfHit,
  dictionaryOf,
  referencesOf,
  resolveOne,
  typeCandidates,
} from '../model/references';
import type {
  ElementTypeEntry,
  Reference,
  ReferenceCandidate,
  Resolution,
} from '../model/references';
import type { OmniboxResult, ParsedCommand } from '../model/types';
import { searchKeys } from './useSearch';

/**
 * Rozwiązuje nazwy z komendy na identyfikatory (spec 6.1).
 *
 * ⭐ Elementy jadą **tym samym endpointem, którym szuka omnibox** — a nie osobnym „lookupem".
 * Powód nie jest oszczędnościowy: gdyby rozwiązywanie miało własne dopasowanie, `>Płatnosci`
 * mogłoby wskazać inny element niż ten, który człowiek widzi na liście trafień po wpisaniu
 * `Płatnosci`. Jedno źródło dopasowania to jedyny sposób, żeby to się nie rozjechało.
 *
 * ⭐ Właściciele jadą **katalogiem organizacji** (`/api/org`, decyzja 79), bo userzy i zespoły
 * to druga rodzina bytów (spec sek. 3) i nie ma ich w indeksie elementów. Tu rozjazd nie grozi,
 * bo omnibox nie pokazuje bytów organizacyjnych jako trafień — one wychodzą wyłącznie jako
 * kandydaci przy nazwie właściciela.
 *
 * ⭐ Typ jedzie **słownikiem typów** (`/api/element-types`, decyzja 83). To domyka usterkę,
 * którą widać było na ekranie: parser (`Search`) nie zna słownika (`Graph`), więc lustro
 * pisało „gotowe do zapisu" przy typie, którego serwer nie przyjmie. Wiedza o słowniku wchodzi
 * tam, gdzie i tak stoi rozstrzyganie nazw, zamiast jako drugi, osobny mechanizm sprawdzania.
 *
 * Zapytania idą **równolegle** (`useQueries`) i dzielą pamięć podręczną z resztą aplikacji,
 * więc nazwa, którą przed chwilą widziano, zwykle nie kosztuje nawet przelotu.
 */
export function useReferences(command: ParsedCommand | undefined, enabled: boolean) {
  const references: Reference[] = useMemo(
    () => (command ? referencesOf(command) : []),
    [command],
  );

  const results = useQueries({
    queries: references.map((reference) => {
      const enabledHere = enabled && reference.name.length > 0;
      const dictionary = dictionaryOf(reference.role);

      if (dictionary === 'org') {
        return { ...orgQueryOptions(reference.name), enabled: enabledHere };
      }

      if (dictionary === 'types') {
        // ⚠ **Cały słownik, także wycofane pozycje** (`activeOnly: false`). Podpowiadamy
        // wyłącznie czynne, ale żeby powiedzieć „ten typ jest wycofany" zamiast „nie ma
        // takiego typu", trzeba wycofane w ogóle widzieć. Serwer rozróżnia te dwa przypadki
        // dwoma kodami błędu — front, który blokuje Enter wcześniej, musi to zrobić sam.
        return { ...elementTypesQueryOptions(false), enabled: enabledHere };
      }

      return {
        queryKey: searchKeys.omnibox(reference.name),
        queryFn: ({ signal }: { signal: AbortSignal }) =>
          searchApi.fetchOmnibox(reference.name, signal),
        enabled: enabledHere,
        staleTime: 30_000,
      };
    }),
  });

  const isPending = results.some((result) => result.isPending);

  // ⚠ Bez `useMemo` i to jest świadome: `useQueries` zwraca NOWĄ tablicę przy każdym
  // renderze, więc każda tablica zależności oparta na niej i tak byłaby nowa. Zapamiętywanie
  // dawałoby złudzenie stabilności, nie stabilność. Referencji jest kilka, a `resolveOne`
  // to porównanie tekstów — liczenie wprost jest tańsze niż udawana pamięć podręczna.
  const resolutions: Resolution[] = references.map((reference, index) => {
    const candidates = candidatesOf(reference, results[index]?.data);

    // Dopóki odpowiedź nie przyszła, referencja NIE jest „brakująca" — to dwa różne
    // stany, a pomylenie ich pokazywałoby „nie ma takiego" przy każdej literze.
    if (candidates === undefined) {
      return { key: reference.key, state: 'ambiguous', candidates: [] };
    }

    return resolveOne(reference, candidates);
  });

  // Surowi kandydaci per referencja — pod PODPOWIEDZI przy pisaniu.
  //
  // ⚠ To nie to samo, co rozstrzygnięcie: nazwa może być już jednoznacznie rozwiązana,
  // a człowiek i tak chce zobaczyć, co dokładnie zostało dopasowane, i móc to zmienić.
  // Bez tego `>bram` po cichu wskazywało „Bramka BLIK" i nic tego nie pokazywało.
  const candidatesByKey = new Map<string, readonly ReferenceCandidate[]>();

  references.forEach((reference, index) => {
    candidatesByKey.set(reference.key, candidatesOf(reference, results[index]?.data) ?? []);
  });

  // Słownik typów w surowej postaci — wyłącznie po to, żeby wołający umiał nazwać typ
  // WYCOFANY, którego celowo nie ma wśród kandydatów. Bez tego omnibox musiałby zadać
  // to samo zapytanie drugi raz, tylko po to, żeby dobrać jedno zdanie.
  const typeIndex = references.findIndex((reference) => reference.role === 'type');
  const typeDictionary: readonly ElementTypeEntry[] =
    (typeIndex >= 0 ? typesOf(results[typeIndex]?.data) : undefined) ?? [];

  return { references, resolutions, candidatesByKey, typeDictionary, isPending };
}

/** Odpowiedź katalogu w kształcie, którego oczekuje jego rola. Patrz `candidatesOf`. */
type CatalogueData = OmniboxResult | OrgRef[] | ElementType[] | undefined;

/**
 * Odpowiedź katalogu na kandydatów.
 *
 * ⚠ **Kształt odpowiedzi rozstrzyga `dictionaryOf`, a nie oglądanie danych.** Wcześniej
 * wystarczało `Array.isArray` (szukanie zwraca obiekt, katalog organizacji — listę), ale
 * słownik typów też jest listą, więc rozpoznawanie po kształcie stałoby się zgadywaniem.
 * Ta sama funkcja wybiera zapytanie i czyta jego wynik — czyli nie ma dwóch miejsc, które
 * mogłyby mieć różne zdanie.
 *
 * `undefined` znaczy „jeszcze nie wiem" i musi przeżyć do `resolutions`, bo to nie to samo
 * co „nic nie znalazłem".
 */
function candidatesOf(
  reference: Reference,
  data: CatalogueData,
): readonly ReferenceCandidate[] | undefined {
  if (data === undefined) {
    return undefined;
  }

  switch (dictionaryOf(reference.role)) {
    case 'org':
      return (data as OrgRef[]).map((ref) => ({
        dictionary: 'org' as const,
        title: ref.name,
        ownerKind: ref.kind,
        ownerId: ref.id,
      }));

    case 'types':
      return typeCandidates(reference.name, data as ElementType[]);

    default:
      return (data as OmniboxResult).hits.map(candidateOfHit);
  }
}

/** Ta sama zasada co w `candidatesOf`: o kształcie decyduje rola, nie wygląd danych. */
function typesOf(data: CatalogueData): readonly ElementTypeEntry[] | undefined {
  return data === undefined ? undefined : (data as ElementType[]);
}
